import type { BoxProps } from '@hover/blueprint';
import { Body, Box, Heading } from '@hover/blueprint';
import React, { useState } from 'react';

import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { buttonPressed } from 'src/shared/telemetry';

import type { NavbarItemSchema } from '../../Navbar';
import { NavbarButton } from '../../Navbar/NavbarButton';

const TRACK_ID = 'project-jump-list';

interface ProjectDetailsProps extends BoxProps {
  name: string;
  areas: string[];
  navItems: string[];
  onNavButtonClick: (sectionName: string) => void;
  projectId: number;
}

const navLinks = [
  {
    name: 'about',
    translationKey: 'projects.about',
    mobileOrder: 1,
    desktopOrder: 1,
    trackId: TRACK_ID,
    mobileText: 'about',
  },
  {
    name: 'materials',
    translationKey: 'projects.materials',
    mobileOrder: 2,
    desktopOrder: 2,
    trackId: TRACK_ID,
    mobileText: 'materials',
  },
  {
    name: 'services',
    translationKey: 'projects.services',
    mobileOrder: 3,
    desktopOrder: 3,
    trackId: TRACK_ID,
    mobileText: 'services',
  },
  {
    name: 'help',
    translationKey: 'projects.help',
    mobileOrder: 4,
    desktopOrder: 4,
    trackId: TRACK_ID,
    mobileText: 'help',
  },
] as const satisfies readonly NavbarItemSchema[];

const ProjectDetails = ({
  name,
  navItems,
  areas,
  onNavButtonClick,
  projectId,
  ...rest
}: ProjectDetailsProps) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { trackingData } = useHomeownerAppContext();
  const availableNavLinks = navLinks.filter(link =>
    navItems.includes(link.name),
  );
  const [selectedNavButton, setSelectedNavButton] = useState<
    string | undefined
  >(availableNavLinks[0]?.name);

  const handleNavButtonClick = (link: NavbarItemSchema) => {
    setSelectedNavButton(link.name);
    buttonPressed({
      ...trackingData,
      button_text: link.name,
      button_location: 'Project Jump List',
      primary_cta: false,
      project_id: projectId,
      track_id: link.trackId,
    });
    if (typeof onNavButtonClick === 'function') {
      onNavButtonClick(link.name);
    }
  };

  return (
    <>
      <Box flexDirection="column" {...rest}>
        <Heading mb={50} size={200}>
          {t('projects.project')}
        </Heading>
        <Heading noOfLines={2}>{name}</Heading>
      </Box>
      {availableNavLinks.length ? (
        <Box
          alignItems="center"
          as="nav"
          borderBottom="1px solid lightgray"
          marginBottom="700"
          marginTop="700"
          minW="100%"
          pb="500"
        >
          {availableNavLinks
            .filter(link => navItems.includes(link.name))
            .map(link => (
              <NavbarButton
                color={
                  selectedNavButton === link.name
                    ? 'neutral.900'
                    : 'neutral.600'
                }
                isSelected={link.name === selectedNavButton}
                key={link.name}
                mode="light"
                mr="auto"
                navbarItem={link}
                onClick={() => handleNavButtonClick(link)}
                padding={0}
                tabIndex={link.desktopOrder}
              />
            ))}
        </Box>
      ) : undefined}
      <Box pt={300}>
        <Body isBold pr={100}>
          {t('projects.area')}
        </Body>
        <Body
          color="neutral.600"
          sx={{
            ':first-letter': {
              textTransform: 'capitalize',
            },
          }}
        >
          {areas.join(', ').replaceAll('_', ' ')}
        </Body>
      </Box>
    </>
  );
};

export { ProjectDetails };
